import type { UserId } from 'features/users/types';
import type { EventUser } from 'services/signaling';
import * as z from 'zod';
import { quizSchema } from 'features/quizzes/validation/quizSchema';

export type CreateQuizData = z.infer<typeof quizSchema>;

export type TimingMode = 'quiz' | 'question';

export type QuizStatus = 'created' | 'launched' | 'ended';

export function isQuiz(quiz: unknown): quiz is Quiz {
  return quiz !== null && typeof quiz === 'object' && 'id' in quiz && 'questions' in quiz;
}

export interface QuizChoice {
  text: string;
  correct?: boolean;
}

export interface QuizQuestion {
  id: string;
  text: string;
  multipleChoices: boolean;
  choices: QuizChoice[];
  secondsRemaining?: number;
}

export interface Quiz {
  id: string;
  sessionId: string;
  title: string;
  showCorrectAnswers: boolean;
  timingMode: TimingMode;
  timeLimitSeconds: number;
  status: QuizStatus;
  submitted: boolean;
  completionsCount: number;
  questions: QuizQuestion[];
  createdBy: EventUser;
  totalQuestions?: number;
  totalSecondsRemaining?: number;
  editable?: boolean;
  passingScorePercentage: number;
}

export type LaunchedQuizPayload = Omit<Quiz, 'questions'> & {
  questions: Array<
    QuizQuestion & {
      selectedChoices?: number[];
    }
  >;
};

export type QuizEntities = Record<string, Quiz>;
export type QuizIds = string[];

export interface ParticipantScore {
  user: EventUser & {
    externalId?: string;
  };
  correctSubmissions: number;
  passed: boolean;
}

export interface QuizResultsData {
  id: string;
  totalQuestions: number;
  totalCompletions: number;
  results: ParticipantScore[];
}

export interface QuizResults {
  status: 'idle' | 'loading' | 'success' | 'error';
  data: QuizResultsData | null;
}

export interface QuestionResult {
  id: string;
  correctChoices: number[];
  choices?: number[];
}

export interface ParticipantDetailedResultsData {
  id: string;
  user: EventUser;
  correctSubmissions: number;
  passed: boolean;
  questions: QuestionResult[];
}

export interface DetailedQuizResults {
  status: 'idle' | 'loading' | 'success' | 'error';
  data: ParticipantDetailedResultsData | null;
}

export interface QuizTemplateChoice {
  text: string;
  correct: boolean;
}

export interface QuizTemplateQuestion {
  id: string;
  text: string;
  multipleChoices: boolean;
  choices: QuizTemplateChoice[];
}

export interface QuizTemplate {
  id: string;
  title: string;
  timingMode: TimingMode;
  timeLimitSeconds: number;
  showCorrectAnswers: boolean;
  passingScorePercentage: number;
  questions: QuizTemplateQuestion[];
}

export type QuizTemplateEntities = Record<string, QuizTemplate>;
export type QuizTemplateIds = string[];

export interface QuizzesState {
  exportProcessing: boolean;
  activeQuizId: string | null;
  activeQuizSessionId: string | null;
  entities: QuizEntities;
  ids: QuizIds;

  activeQuiz: {
    localSelections: Record<string, number[]>;
    launchedAt: number | null;
    currentQuestionIndex: number;
  };

  results: Record<string, QuizResults>;
  detailedResults: Record<string, Record<UserId, DetailedQuizResults>>;

  templates: {
    entities: QuizTemplateEntities;
    ids: QuizTemplateIds;
  };
}

export interface QuizQuestionPayload {
  text: string;
  multipleChoices: boolean;
  choices: Array<{
    text: string;
    correct: boolean;
  }>;
}

export interface QuizPayload {
  title: string;
  timeLimitSeconds: number;
  timingMode: TimingMode;
  showCorrectAnswers: boolean;
  passingScorePercentage?: number;
  questions: QuizQuestionPayload[];
}

export interface EditQuizPayload extends QuizPayload {
  id: string;
}

export interface QuizAnswerPayload {
  id: string;
  questionId: string;
  choices: number[];
}

export interface QuizTemplateQuestionPayload {
  text: string;
  multipleChoices: boolean;
  choices: Array<{
    text: string;
    correct: boolean;
  }>;
}

export interface CreateQuizTemplatePayload {
  title: string;
  timeLimitSeconds: number;
  timingMode: TimingMode;
  showCorrectAnswers: boolean;
  passingScorePercentage?: number;
  questions: QuizTemplateQuestionPayload[];
}

export interface EditQuizTemplatePayload extends CreateQuizTemplatePayload {
  id: string;
}
